import { Box, Flex, Input, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { TColorsHooks } from './useColors';

interface IProps {
    showColorPicker: boolean;
    setShowColorPicker: (value: boolean) => void;
    colorToSubmit: TColorsHooks['colorToSubmit'];
    changeColor: (color: string) => void;
}

const inputStyles = {
    backgroundColor: 'white',
    maxWidth: '60px',
    height: '30px',
    fontSize: 'sm',
    margin: '0 5px 5px 5px',
};

const hexToRgb = (hex: string) => {
    let formattedHex = hex.replace('#', '');
    if (formattedHex.length === 3) {
        formattedHex = [...formattedHex].map((char) => char + char).join('');
    }
    const bigint = parseInt(formattedHex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return { r, g, b };
};

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) => {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const ColorPicker = ({ showColorPicker, setShowColorPicker, colorToSubmit, changeColor }: IProps) => {
    const [colorFormat, setColorFormat] = useState<'rgb' | 'hex'>('hex');
    const [rgb, setRgb] = useState<{ r: number; g: number; b: number }>({
        r: 0,
        g: 0,
        b: 0,
    });

    useEffect(() => {
        if (colorToSubmit?.color) {
            setRgb(hexToRgb(colorToSubmit.color));
        }
    }, [colorToSubmit?.color]);

    const onRGBChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let { value } = e.target;
        const { name } = e.target;
        if (Number(value) > 255) value = '255';
        changeColor(rgbToHex({ ...rgb, [name]: Number(value) }));
    };

    return (
        <Flex
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg={showColorPicker ? '#F5F5F5' : 'white'}
            border={showColorPicker ? 'none' : '1px solid #E2E8F0'}
            borderRadius="20px"
            p="20px"
            flexDirection="column"
            transition="all 0.4s ease-in-out"
        >
            <Flex
                minW="100%"
                alignItems="center"
                mb={showColorPicker ? '16px' : '0'}
                cursor="pointer"
                onClick={() => setShowColorPicker(!showColorPicker)}
            >
                <Box
                    width="30px"
                    height="30px"
                    borderRadius="50%"
                    bg={colorToSubmit?.color}
                    display="inline-block"
                ></Box>
                <Text fontSize="sm" color="gray.500" ml="8px">
                    {colorToSubmit?.color}
                </Text>
            </Flex>
            {showColorPicker && (
                <>
                    <HexColorPicker color={colorToSubmit?.color} onChange={changeColor} />

                    <Flex alignItems="center" marginTop="16px" position="relative">
                        {colorFormat === 'hex' ? (
                            <>
                                <Text
                                    position="absolute"
                                    left="10px"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    fontSize="14px"
                                >
                                    #
                                </Text>
                                <HexColorInput
                                    color={colorToSubmit?.color}
                                    onChange={changeColor}
                                    style={{
                                        fontSize: '14px',
                                        backgroundColor: 'white',
                                        maxWidth: '200px',
                                        minWidth: '200px',
                                        height: '30px',
                                        border: '1px solid #CAC5C4',
                                        padding: '2px 20px',
                                    }}
                                />
                            </>
                        ) : (
                            <Flex>
                                <Flex flexDirection="column" alignItems="center">
                                    <Input
                                        type="number"
                                        name="r"
                                        value={rgb?.r}
                                        {...inputStyles}
                                        onChange={onRGBChange}
                                    />
                                    <Text fontSize="14px" color="gray.500" mx="8px">
                                        R
                                    </Text>
                                </Flex>
                                <Flex flexDirection="column" alignItems="center">
                                    <Input
                                        type="number"
                                        name="g"
                                        value={rgb?.g}
                                        {...inputStyles}
                                        onChange={onRGBChange}
                                    />
                                    <Text fontSize="14px" color="gray.500" mx="8px">
                                        G
                                    </Text>
                                </Flex>
                                <Flex flexDirection="column" alignItems="center">
                                    <Input
                                        type="number"
                                        name="b"
                                        value={rgb?.b}
                                        {...inputStyles}
                                        onChange={onRGBChange}
                                    />
                                    <Text fontSize="14px" color="gray.500" mx="8px">
                                        B
                                    </Text>
                                </Flex>
                            </Flex>
                        )}
                    </Flex>
                    <Text
                        fontSize="sm"
                        mt="8px"
                        color="gray.500"
                        cursor="pointer"
                        onClick={() => setColorFormat(colorFormat === 'hex' ? 'rgb' : 'hex')}
                    >
                        <Text as="u"> Switch to {colorFormat === 'hex' ? 'RGB' : 'HEX'}</Text>
                    </Text>
                </>
            )}
        </Flex>
    );
};

export default ColorPicker;
